import { Chip } from "@mui/material";
import { GridRowId } from "@mui/x-data-grid";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { MutableRefObject, createElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons/lib";
import { TbCar, TbTag } from "react-icons/tb";

import CommonAlert from "@/components/CommonAlert";
import { QUERY_KEYS } from "@/constants";
import { IHotelClinicPassengerType } from "@/models/hotel-clinic.model";
import type { HotelClinicBulkPassengerPayload } from "@/pages/HotelPlanning/clinic-arrival/utils/hotelClinicCreateTaskPayload";
import type { Req_UpdateHotelClinicRequestPayload } from "@/requests/hotel-clinic-request-update.types";
import type { Req_UpdateHotelClinicTaskPassengerPayload } from "@/requests/hotel-clinic-task-update.types";
import {
    useCreateHotelClinicTasksInBulkMutation,
    useSendNotificationTemplatesMutation,
    useUpdateHotelClinicRequestsLabelMutation,
    useUpdateHotelClinicTasksMutation,
} from "@/requests/hotel-clinic.mutations";
import { HotelDepartureChangesPayloadItem, useSaveHotelDepartureChanges } from "@/requests/hotel-departure.queries";
import type { Req_NotificationTemplateItem } from "@/requests/notification-templates.types";
import type { ICommonListResponse } from "@/schemas/commonList";
import {
    HOTEL_DEPARTURE_META_KEYS,
    HotelEditableFields,
    useHotelPlanningDepartureActions,
    useHotelPlanningDepartureChanges,
    useHotelPlanningDepartureShowConfirmationList,
} from "@/stores/use-hotel-planning-departure-table";

function HotelDepartureUpdateDialog({ apiRef }: { apiRef: MutableRefObject<GridApiPremium> }) {
    const showConfirmationList = useHotelPlanningDepartureShowConfirmationList();
    const changes = useHotelPlanningDepartureChanges();
    const { t } = useTranslation();
    const { handleShowConfirmationList, handleRowModesModelCancel } = useHotelPlanningDepartureActions();
    const queryClient = useQueryClient();

    // clinic rows → checkpoint-svc; custom rows → transport-svc (create task, or reassign an existing one).
    const { mutateAsync: saveClinicChanges, isPending: savingClinic } = useSaveHotelDepartureChanges();
    const { mutateAsync: createCustomTasks, isPending: creatingCustom } = useCreateHotelClinicTasksInBulkMutation();
    // isoDate undefined → skip its clinic-arrival invalidation; we invalidate the departure queries ourselves below.
    const { mutateAsync: updateCustomTasks, isPending: updatingCustom } = useUpdateHotelClinicTasksMutation({
        isoDate: undefined,
    });
    // custom rows' labelledUser lives on the request, independent of task/driver assignment.
    const { mutateAsync: updateRequestLabels, isPending: updatingLabels } = useUpdateHotelClinicRequestsLabelMutation();
    const { mutateAsync: sendNotificationTemplates } = useSendNotificationTemplatesMutation();
    const isPending = savingClinic || creatingCustom || updatingCustom || updatingLabels;

    const changeList = useMemo(
        () =>
            showConfirmationList.reduce(
                (prev, curr) => {
                    const keys = Object.keys(changes[curr] || {}).filter(
                        (key) => !HOTEL_DEPARTURE_META_KEYS.includes(key as keyof HotelEditableFields)
                    );

                    keys.forEach((key) => prev.list.add(key as keyof HotelEditableFields));
                    return prev;
                },
                {
                    list: new Set<keyof HotelEditableFields>(),
                }
            ),
        [showConfirmationList, changes]
    );

    const handleConfirm = async () => {
        if (isPending || !showConfirmationList.length) return;

        const clinicOps: HotelDepartureChangesPayloadItem[] = [];
        const customCreate: HotelClinicBulkPassengerPayload[] = [];
        const customUpdate: Req_UpdateHotelClinicTaskPassengerPayload[] = [];
        const labelOps: Req_UpdateHotelClinicRequestPayload[] = [];
        // One entry per requestId with a label change; turned into notifications once we know which PATCHed ok.
        const labelNotificationPlans: {
            requestId: string;
            newDriverId: string | null;
            previousDriverId: string | null;
        }[] = [];
        // Map each mutation's per-item response `id` back to its grid row id.
        const keyToRowId = new Map<string, GridRowId>();

        for (const id of showConfirmationList) {
            const rowChanges = changes[id];
            if (!rowChanges) continue;
            const { assignedUser, uniqueCheckpointId, labelledUser, previousLabelledUserId, ctx } = rowChanges;

            if (!ctx || ctx.type === "clinic") {
                clinicOps.push({
                    assignedUserId: assignedUser || (assignedUser === null ? null : undefined),
                    operationId: id as string,
                    uniqueCheckpointId: uniqueCheckpointId ?? "",
                    labelledUserId: labelledUser || (labelledUser === null ? null : undefined),
                });
                keyToRowId.set(id as string, id);

                if (labelledUser !== undefined) {
                    labelNotificationPlans.push({
                        requestId: id as string,
                        newDriverId:
                            typeof labelledUser === "string" && labelledUser.trim() ? labelledUser.trim() : null,
                        previousDriverId: previousLabelledUserId ?? null,
                    });
                }
                continue;
            }

            // Custom row: driver (task) and label (request) are independent write targets.
            if (labelledUser !== undefined) {
                const newDriverId =
                    typeof labelledUser === "string" && labelledUser.trim() ? labelledUser.trim() : null;
                labelOps.push({
                    requestId: ctx.passengerDbId,
                    updateData: { labelledDriverUserId: newDriverId },
                });
                labelNotificationPlans.push({
                    requestId: ctx.passengerDbId,
                    newDriverId,
                    previousDriverId: previousLabelledUserId ?? null,
                });
                keyToRowId.set(ctx.passengerDbId, id);
            }

            if (assignedUser !== undefined) {
                // Custom driver edit: assign/reassign, or clear = delete the task (unassign).
                const driverUserId = typeof assignedUser === "string" && assignedUser.trim() ? assignedUser.trim() : "";

                if (ctx.taskId) {
                    // Existing task → reassign the driver, or delete the task when the driver is cleared.
                    customUpdate.push({
                        taskId: ctx.taskId,
                        updateData: driverUserId ? { driverUserId } : { deleteTask: true },
                    });
                    keyToRowId.set(ctx.taskId, id);
                } else if (driverUserId) {
                    // No task yet → create one. (Cleared with no task = nothing to do.)
                    customCreate.push({
                        type: IHotelClinicPassengerType.CUSTOM,
                        driverUserId,
                        passengerDbId: ctx.passengerDbId,
                    });
                    keyToRowId.set(ctx.passengerDbId, id);
                }
            }
        }

        if (!clinicOps.length && !customCreate.length && !customUpdate.length && !labelOps.length) {
            handleClose();
            return;
        }

        const succeededRowIds: GridRowId[] = [];
        let failedCount = 0;

        const collect = (responses: ICommonListResponse) => {
            for (const r of responses) {
                const rowId = keyToRowId.get(r.id) ?? r.id;
                if (r.ok) {
                    succeededRowIds.push(rowId);
                } else {
                    failedCount += 1;
                    Sentry.captureException(new Error(r.message));
                    enqueueSnackbar(r.message || "", { variant: "error" });
                }
            }
        };

        // Per-bucket try/catch so one failing endpoint doesn't discard the others' results.
        const runBucket = async (p: Promise<ICommonListResponse>) => {
            try {
                collect(await p);
            } catch (error) {
                console.error("HotelDepartureUpdateDialog save error:", error);
                if (error instanceof Error) enqueueSnackbar(error.message, { variant: "error" });
            }
        };

        // Captured separately from `collect` so we know exactly which requestIds to notify for.
        let labelResponses: ICommonListResponse | null = null;
        const runLabelBucket = async () => {
            try {
                const responses = await updateRequestLabels({ requests: labelOps });
                labelResponses = responses;
                collect(responses);
            } catch (error) {
                console.error("HotelDepartureUpdateDialog label save error:", error);
                if (error instanceof Error) enqueueSnackbar(error.message, { variant: "error" });
            }
        };

        // Same capture for the clinic (checkpoint-svc) bucket — its labelledUser edits notify too.
        let clinicResponses: ICommonListResponse | null = null;
        const runClinicBucket = async () => {
            try {
                const responses = await saveClinicChanges({ operations: clinicOps });
                clinicResponses = responses;
                collect(responses);
            } catch (error) {
                console.error("HotelDepartureUpdateDialog clinic save error:", error);
                if (error instanceof Error) enqueueSnackbar(error.message, { variant: "error" });
            }
        };

        const buckets: Promise<void>[] = [];
        if (clinicOps.length) buckets.push(runClinicBucket());
        // Departure is a pool → uniqueTask: true keeps each custom passenger in its own task.
        if (customCreate.length)
            buckets.push(runBucket(createCustomTasks({ passengers: customCreate, uniqueTask: true })));
        if (customUpdate.length) buckets.push(runBucket(updateCustomTasks({ tasks: customUpdate, uniqueTask: true })));
        if (labelOps.length) buckets.push(runLabelBucket());
        await Promise.all(buckets);

        // Label PATCH always fires before its notification, and only for rows that actually changed.
        if (labelResponses || clinicResponses) {
            const allResponses = [
                ...((labelResponses as ICommonListResponse | null) ?? []),
                ...((clinicResponses as ICommonListResponse | null) ?? []),
            ];
            const succeededRequestIds = new Set(allResponses.filter((r) => r.ok).map((r) => r.id));
            const notifications: Req_NotificationTemplateItem[] = [];
            for (const plan of labelNotificationPlans) {
                if (!succeededRequestIds.has(plan.requestId)) continue;
                const patientName = apiRef.current.getRow(keyToRowId.get(plan.requestId) ?? plan.requestId)?.name ?? "";
                const context = { requestId: plan.requestId, name: patientName };
                if (plan.previousDriverId) {
                    notifications.push({
                        subCategory: "transport_svc_hotel_clinic_label_unassign",
                        context,
                        extraRecipients: { userIds: [plan.previousDriverId] },
                    });
                }
                if (plan.newDriverId) {
                    notifications.push({
                        subCategory: "transport_svc_hotel_clinic_label_assign",
                        context,
                        extraRecipients: { userIds: [plan.newDriverId] },
                    });
                }
            }

            if (notifications.length) {
                try {
                    const notificationResponses = await sendNotificationTemplates({ notifications });
                    for (const r of notificationResponses) {
                        if (!r.ok) {
                            console.error("notification-templates item failed:", r.message);
                            Sentry.captureException(new Error(r.message));
                        }
                    }
                } catch (error) {
                    console.error("notification-templates call failed:", error);
                    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
                }
            }
        }

        // A row can land in two buckets (e.g. label + driver edited together) — dedupe before teardown.
        const uniqueSucceededRowIds = [...new Set(succeededRowIds)];

        // Tear down each succeeded row's open editor BEFORE refetch (must run while the row still exists).
        for (const rowId of uniqueSucceededRowIds) {
            handleRowModesModelCancel(rowId, apiRef.current, false);
        }

        // Refetch both sources — clinic (checkpoint-svc) and custom (transport-svc).
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_HOTEL_DEPARTURE_PLANNING] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_HOTEL_DEPARTURE_CUSTOM_PASSENGERS] });

        if (uniqueSucceededRowIds.length && !failedCount) {
            enqueueSnackbar(
                t("responses.passengerUpdated", {
                    count: uniqueSucceededRowIds.length > 1 ? 2 : 1,
                    length: uniqueSucceededRowIds.length,
                }),
                { variant: "success" }
            );
        }

        handleClose();
    };

    const handleClose = () => {
        handleShowConfirmationList([]);
    };

    return (
        <CommonAlert
            open={!!showConfirmationList.length}
            loading={isPending}
            description="confirmations.updatePassenger"
            color="primary"
            onClose={handleClose}
            handleConfirm={handleConfirm}
            html
            descriptionOpts={{
                count: showConfirmationList.length,
            }}
        >
            <div className="flex gap-2 flex-wrap py-1">
                {[...changeList.list].map((label) => (
                    <PlanUpdateDialogChip key={label} label={label} />
                ))}
            </div>
        </CommonAlert>
    );
}

export const editableOptions: Partial<
    Record<
        keyof HotelEditableFields,
        {
            icon: IconType;
        }
    >
> = {
    assignedUser: {
        icon: TbCar,
    },
    uniqueCheckpointId: {
        icon: TbCar,
    },
    labelledUser: {
        icon: TbTag,
    },
};

export function PlanUpdateDialogChip({ label }: { label: keyof HotelEditableFields }) {
    const { t } = useTranslation();
    const item = editableOptions[label];

    if (!item) return null;

    return <Chip icon={createElement(item.icon)} label={t(label)} />;
}
export default HotelDepartureUpdateDialog;
